// API utility functions for consistent API calls
import { getAuth } from 'firebase/auth';

// Use relative path - works on both localhost and Vercel
const API_BASE = '/api';


// Helper function to make API calls
export const apiCall = async (endpoint, options = {}) => {
  const auth = getAuth();

  const user = auth.currentUser;
  const token = await user.getIdToken(true); // 👈 FORCE REFRESH
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers:{
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

// Auth API calls
export const authAPI = {
  register: (name, email, password, role) =>
    apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, role })
    }),
  login: (email, password) =>
    apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    }),
  logout: () => apiCall('/auth/logout', { method: 'POST' }),
  getProfile: () => apiCall('/auth/profile')
};

// Course API calls
export const courseAPI = {
  getAll: () => apiCall('/courses'),
  getById: (id) => apiCall(`/courses/${id}`),
  create: (data) =>
    apiCall('/courses', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  update: (id, data) =>
    apiCall(`/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
  delete: (id) =>
    apiCall(`/courses/${id}`, { method: 'DELETE' }),
  enroll: (id) =>
    apiCall(`/courses/${id}/enroll`, { method: 'POST' }),
  getEnrolled: () => apiCall('/courses/user/enrolled')
};

// Assignment API calls
export const assignmentAPI = {
  getByCourse: (courseId) =>
    apiCall(`/assignments/course/${courseId}`),
  getById: (id) => apiCall(`/assignments/${id}`),
  create: (data) =>
    apiCall('/assignments', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  update: (id, data) =>
    apiCall(`/assignments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
  delete: (id) =>
    apiCall(`/assignments/${id}`, { method: 'DELETE' }),
  submit: (id, data) =>
    apiCall(`/assignments/${id}/submit`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  grade: (id, data) =>
    apiCall(`/assignments/${id}/grade`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
};

// Quiz API calls
export const quizAPI = {
  getByCourse: (courseId) =>
    apiCall(`/quizzes/course/${courseId}`),
  getById: (id) => apiCall(`/quizzes/${id}`),
  create: (data) =>
    apiCall('/quizzes', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  update: (id, data) =>
    apiCall(`/quizzes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
  delete: (id) =>
    apiCall(`/quizzes/${id}`, { method: 'DELETE' }),
  submit: (id, data) =>
    apiCall(`/quizzes/${id}/submit`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  getResults: (id) => apiCall(`/quizzes/${id}/results`)
};

// Certificate API calls
export const certificateAPI = {
  getAll: () => apiCall('/certificates'),
  getById: (id) => apiCall(`/certificates/${id}`),
  generate: (data) =>
    apiCall('/certificates/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  download: (id) => apiCall(`/certificates/${id}/download`)
};

// Forum API calls
export const forumAPI = {
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    const query = searchParams.toString();
    return apiCall(`/forums${query ? `?${query}` : ''}`);
  },
  getById: (id) => apiCall(`/forums/${id}`),
  create: (data) =>
    apiCall('/forums', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  update: (id, data) =>
    apiCall(`/forums/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
  remove: (id) => apiCall(`/forums/${id}`, { method: 'DELETE' }),
  getPosts: (id) => apiCall(`/forums/${id}/posts`),
  createPost: (forumId, data) =>
    apiCall(`/forums/${forumId}/posts`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  updatePost: (postId, data) =>
    apiCall(`/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
  deletePost: (postId) => apiCall(`/posts/${postId}`, { method: 'DELETE' }),
  toggleLike: (postId) =>
    apiCall(`/posts/${postId}/like`, {
      method: 'POST'
    }),
  getUserGroups: () => apiCall('/user/groups')
};

const api = {
  authAPI,
  courseAPI,
  assignmentAPI,
  quizAPI,
  certificateAPI,
  forumAPI
};

export default api;
